import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportMailbox } from '../../../queries/Support/useSupportMailboxQuery';
import { PlusIconThin } from '../../../web/components/svgs/icons/PlusIconThin';
import { Button } from '../../../web/components-v2/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { InboxGlyph, ReportsGlyph, SettingsGlyph } from '../../pages/AdminSupportInboxPage/inboxIcons';
import { supportSharedStyles as ss } from './styles/supportSharedStyles';

export type SupportMastheadTab = 'inbox' | 'reports' | 'settings';

interface SupportMastheadAppBarProps {
	activeTab: SupportMastheadTab;
	mailboxes: ISupportMailbox[];
	selectedMailboxId: string;
	onSelectInbox: () => void;
	onSelectReports: () => void;
	onSelectSettings: () => void;
	onSelectMailbox: (mailboxId: string) => void;
	/** Renders a "New ticket" action in the masthead when provided. */
	onNewTicket?: () => void;
	/**
	 * Show the mailbox switcher in the masthead. The inbox screen relocates it into the mailbox
	 * panel's channel selector, so it hides it here to avoid a duplicate control.
	 */
	showMailboxSwitcher?: boolean;
	showSettings?: boolean;
	children?: React.ReactNode;
}

/**
 * Shared support desk masthead chrome. Keeps Inbox / Reports / Settings visible across support routes
 * so nested settings pages do not replace the app-level tabs with breadcrumbs.
 */
export function SupportMastheadAppBar({
	activeTab,
	mailboxes,
	selectedMailboxId,
	onSelectInbox,
	onSelectReports,
	onSelectSettings,
	onSelectMailbox,
	onNewTicket,
	showMailboxSwitcher = true,
	showSettings = true,
	children,
}: SupportMastheadAppBarProps) {
	const selectedMailbox = mailboxes.find(m => m.id === selectedMailboxId);
	const tabStyles = (tab: SupportMastheadTab) =>
		activeTab === tab ? [ss.supportTab, ss.supportTabActive] : [ss.supportTab];

	return (
		<div className={css(ss.supportAppBar)}>
			<div className={css(ss.supportTabRow)}>
				<Button type='button' variant='ghost' styles={tabStyles('inbox')} onClick={onSelectInbox}>
					<InboxGlyph size={16} />
					Inbox
				</Button>
				<Button type='button' variant='ghost' styles={tabStyles('reports')} onClick={onSelectReports}>
					<ReportsGlyph size={16} />
					Reports
				</Button>
				{showSettings && (
					<Button type='button' variant='ghost' styles={tabStyles('settings')} onClick={onSelectSettings}>
						<SettingsGlyph size={16} />
						Settings
					</Button>
				)}
			</div>
			{showMailboxSwitcher && (
				<>
					<div className={css(ss.supportTabDivider)} />
					{mailboxes.length > 1 ? (
						<Select
							items={mailboxes.map(m => ({ value: m.id, label: m.name }))}
							value={selectedMailboxId}
							onValueChange={onSelectMailbox}
						>
							<SelectTrigger>
								<SelectValue placeholder='Mailbox' />
							</SelectTrigger>
							<SelectContent>
								{mailboxes.map(m => (
									<SelectItem key={m.id} value={m.id}>
										{m.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					) : (
						<div className={css(ss.supportMailboxSwitcher)}>
							<span className={css(ss.supportMailboxDot)} />
							{selectedMailbox?.name ?? 'Mailbox'}
						</div>
					)}
				</>
			)}
			{onNewTicket && (
				<Button type='button' variant='primary' size='sm' styles={[ss.newTicketButton]} onClick={onNewTicket}>
					<PlusIconThin fillColor='currentColor' width={10} height={10} />
					New ticket
				</Button>
			)}
			{children}
		</div>
	);
}
